import React from 'react';

import { useExternalNavigationContext, useStackNavigationContext, useTabNavigationContext } from 'core/navigation';
import { isNullOrUndefined } from 'core/util';
import { ScreenContainer } from 'ui/component';
import { StyleUtil } from 'ui/util';
import { useAnalyticsContext } from '../../analytics';
import { useConfigContext } from '../../config';
import { CaseContactUs } from '../case-contact-us';
import { CaseFooter } from '../case-footer';
import { CaseHeader } from '../case-header';
import classNames from './EABD2CaseScreenContainer.module.scss';

export interface CaseScreenContainerProps
  extends Readonly<{
    tabs: string[];
    onNextCaseButtonPress?: () => void;
    children?: React.ReactNode;
  }> {}

export const CaseScreenContainer = React.memo<CaseScreenContainerProps>((props) => {
  const { tabs, onNextCaseButtonPress, children } = props;

  const { getContactUsUrl } = useConfigContext();
  const { getServiceInstance } = useExternalNavigationContext();
  const { canPop, pop, reset } = useStackNavigationContext();
  const { screens, selectedScreenIndex, select } = useTabNavigationContext();
  const analyticsContext = useAnalyticsContext();

  // Effect: when the number of tabs and screens not matching
  // we are throwing an error
  React.useEffect(() => {
    if (tabs.length !== screens.length) {
      throw new Error('CaseScreenContainer: the number of tabs and screens not matching!');
    }
  }, [tabs, screens]);

  const ExternalNavigatorService = React.useMemo(() => getServiceInstance(), [getServiceInstance]);

  const isLastTab = React.useMemo(() => selectedScreenIndex === tabs.length - 1, [selectedScreenIndex, tabs]);

  const IsLastCase = React.useMemo(() => isNullOrUndefined(onNextCaseButtonPress), [onNextCaseButtonPress]);

  const OnHomeIconButtonPressCallback = React.useCallback(() => reset(), [reset]);

  const OnBackButtonPressCallback = React.useCallback(() => {
    if (selectedScreenIndex > 0) {
      select(selectedScreenIndex - 1);
      return;
    }

    canPop ? pop() : reset();
  }, [selectedScreenIndex, canPop, select, pop, reset]);

  const OnNextButtonPressCallback = React.useCallback(() => select(selectedScreenIndex + 1), [selectedScreenIndex, select]);

  const OnNextCaseButtonPressCallback = React.useCallback(() => onNextCaseButtonPress && onNextCaseButtonPress(), [onNextCaseButtonPress]);

  const OnContactUsButtonPressCallback = React.useCallback(() => {
    analyticsContext.onContactUsButtonPress();
    ExternalNavigatorService.navigate(getContactUsUrl(), { target: '_blank' });
  }, [analyticsContext, ExternalNavigatorService, getContactUsUrl]);

  return (
    <ScreenContainer>
      <CaseHeader
        tabs={tabs}
        activeTabIndex={selectedScreenIndex}
        onHomeIconButtonPress={OnHomeIconButtonPressCallback}
        onTabSelect={select}
      />
      <div className={classNames.content}>
        {children}
        <div className={StyleUtil.mergeClassNames([classNames.shade, classNames.left])} />
        <div className={StyleUtil.mergeClassNames([classNames.shade, classNames.right])} />
      </div>
      <div className={classNames.contactUsWrapper}>
        <CaseContactUs text={'Would you like to know more?'} buttonText={'Contact us'} onButtonPress={OnContactUsButtonPressCallback} />
      </div>
      <CaseFooter
        isLastTab={isLastTab}
        isLastCase={IsLastCase}
        onBackButtonPress={OnBackButtonPressCallback}
        onNextButtonPress={OnNextButtonPressCallback}
        onNextCaseButtonPress={OnNextCaseButtonPressCallback}
      />
    </ScreenContainer>
  );
});
